import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  GridItem,
  IconMenu,
  Button,
  GridedHeader,
  Chip,
  Typography,
} from '@capitalise/ui-design-system';
import CapitaliseLogo from 'assets/svgs/capitalise_text_logo.svg';
import UserMenu from '../UserMenu';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useAuthContext } from 'HOC/withAuth';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { CompanyBusinessType } from 'enums/company-type';
import { CountryList } from 'enums/country';
import styled from 'styled-components';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { UserType } from 'enums/user-type';
import UpgradeBanner from './UpgradeBanner';
import useBannerContext from 'modules/Business/hooks/useBannerContext';
import { SIDE_MENU_WIDTH } from '../ResponsiveSideMenu';
import DirectorConsentBanner from 'modules/Business/containers/Director/DirectorConsentBanner';
import useDisplayDirectorConsent from 'modules/Business/hooks/useDisplayDirectorConsent';
import { FundingSearchDriverHeader } from 'modules/Business/containers/FundingSearchDriver';
import { MembershipLevelsChips } from 'modules/Business/shared/typesTS';
import DemasqueradeButton from 'RemoteAuth/DemasqueradeButton';
import { PaymentOrigin, useRedirectTrackingPaymentOrigin } from '@capitalise/common';
import CloudConnection from './CloudConnection';
import { createDataTestId } from '@capitalise/utils';

const getUrlOriginMatch = (pathname = '') => {
  switch (true) {
    case pathname.includes('/funding/search'):
      return PaymentOrigin.FUNDING;
    case pathname.includes('/applications'):
    case pathname.includes('/funding'):
      return PaymentOrigin.APPLICATIONS;
    case pathname.includes('/credit-profile'):
      return PaymentOrigin.CREDIT_PROFILE;
    case pathname.includes('/monitor'):
      return PaymentOrigin.MONITOR;
    default:
      return PaymentOrigin.CREDIT_PROFILE;
  }
};

const Header = ({ menuClickCallback, isOpen, injectOrigin, contentRef }) => {
  const { user: auth, logout, demasquerade, isPro, membershipLevel } = useAuthContext();
  const { isMobile } = useMediaQuery();
  const history = useHistory();
  const { redirectToPaymentFlow } = useRedirectTrackingPaymentOrigin();
  const businessName = auth?.company?.name;
  const [didScroll, setDidScroll] = useState(false);

  const userName = auth?.name;
  const userEmail = auth?.email;
  const registrationNumber = auth?.company?.registrationNumber;
  const entityId = auth?.company?.company_id;
  const countryCode = auth?.locale?.countryCode;
  const masqueradeScope = auth?.masqueradeScope;
  const VIEW_AS_CLIENT = 'view-as-client';
  const isMasquerade = auth?.masquerade;
  const isClientView = masqueradeScope === VIEW_AS_CLIENT;
  const isLimitedOrLpp =
    CompanyBusinessType.limitedCompany === auth?.company?.type ||
    CompanyBusinessType.lppCompany === auth?.company?.type ||
    CompanyBusinessType.limitedLiabilityPartnership === auth?.company?.type;
  const isGB = countryCode === CountryList.GREAT_BRITAIN;
  const colorHandler = () => {
    if (isMobile) {
      return 'white';
    }
    if (didScroll) {
      return 'white';
    }
    if (injectOrigin) {
      return 'white';
    }
    return 'transparent';
  };
  const navigateHome = () => {
    history.push('/business/today');
  };
  const location = useLocation();
  const isCreditProfile = location.pathname === '/business/credit-profile';
  const { isBannerShown, setIsBannerShown } = useBannerContext();
  const { showDirectorConsent: shouldDisplayDirectorConsent } = useDisplayDirectorConsent(true);
  const [hidden, setHidden] = useState(false);
  const isBanner = !isPro && !isMobile && !hidden && isCreditProfile;
  const showPlans = !isPro && !isMobile && isGB;
  const MembershipChip = MembershipLevelsChips[membershipLevel];

  const contentScrollHandler = () => {
    setDidScroll(!!contentRef.current.scrollTop);
  };

  useEffect(() => {
    if (contentRef?.current?.addEventListener) {
      contentRef?.current?.addEventListener('scroll', contentScrollHandler);
      return () => contentRef?.current?.removeEventListener('scroll', contentScrollHandler);
    }
  }, [contentRef]);

  useEffect(() => {
    if (isBanner) {
      setIsBannerShown(true);
    } else {
      setIsBannerShown(false);
    }
  }, [isBannerShown, isPro]);

  const handleClose = () => {
    setHidden(true);
    setIsBannerShown(false);
  };

  const FlexGridedHeader = styled(GridedHeader)`
    display: flex;
  `;

  const handleDirectorConsentClick = () => {
    Analytics.track(undefined, AnalyticsEventAction.consentStart, {
      extraParams: {
        consentOrigin: 'credit profile',
      },
    });
    history.push('/business/director');
  };

  const demasqueradeCallback = () => {
    history.push('/monitor');
    window.location.reload();
  };

  const handleComparePlansClick = () => {
    const pathname = location.pathname;
    const origin = getUrlOriginMatch(pathname);
    Analytics.track(AnalyticsEventCategory.business, AnalyticsEventAction.upgradeStart, {
      extraParams: {
        userType: UserType.business,
        companyType: auth?.company?.type,
        membership: auth?.membership?.level?.name,
        upgradeOrigin: 'page header',
      },
    });

    redirectToPaymentFlow(origin, { path: pathname });
  };

  return (
    <FlexGridedHeader>
      <Grid zIndex="18" width="100%" backgroundColor={colorHandler()} paddingX={{ default: 0 }}>
        {shouldDisplayDirectorConsent && (
          <GridItem colSpan={{ sm: 12 }}>
            <DirectorConsentBanner onClick={handleDirectorConsentClick} />
          </GridItem>
        )}
        {isBannerShown && <UpgradeBanner handleClose={handleClose} />}
        <GridItem colSpan={{ sm: 12 }}>
          <FundingSearchDriverHeader didScroll={didScroll} />
        </GridItem>
        {isMobile && isClientView && (
          <GridItem colSpan={{ sm: 12 }} backgroundColor="blue-50" textAlign="center" py="xtiny">
            <Typography size="body-14" color="blue">
              You are now viewing {businessName}
            </Typography>
          </GridItem>
        )}
        <GridItem
          colSpan={{ sm: 12 }}
          backgroundColor={colorHandler()}
          borderBottom="1px solid"
          borderColor="grey-50"
        >
          <Box display="flex" alignItems="center">
            <Box
              display="flex"
              alignItems="center"
              minWidth={!isMobile ? `${SIDE_MENU_WIDTH}px` : 'auto'}
              py="2xsmall"
              pl={{ _: '4xsmall', md: 'xsmall' }}
              backgroundColor="white"
            >
              {isMobile && (
                <Box
                  mr="xtiny"
                  onClick={() => menuClickCallback(!isOpen)}
                  as="button"
                  {...createDataTestId('button', 'business-mobile-menu')}
                >
                  <IconMenu color="green-500" width="xsmall" height="xsmall" />
                </Box>
              )}
              <Box
                mx={{ _: 'tiny', lg: '0' }}
                display="flex"
                alignItems="center"
                cursor="pointer"
                gridGap="tiny"
                onClick={navigateHome}
              >
                <CapitaliseLogo fill="#00933F" />

                {isPro && MembershipChip && <MembershipChip />}
              </Box>
            </Box>
            {isClientView ? (
              <Box
                display="flex"
                justifyContent={!isMobile ? 'space-between' : 'flex-end'}
                width="100%"
                gridGap={{ _: 'tiny', md: '0' }}
                pr={{ _: 'xsmall', md: '0' }}
              >
                {!isMobile && (
                  <Box display="flex" alignItems="center" ml="xsmall">
                    <Chip baseColor="blue" round>
                      You are now viewing {businessName}
                    </Chip>
                  </Box>
                )}
                <DemasqueradeButton
                  demasqueradeCallback={demasqueradeCallback}
                  isMobile={isMobile}
                />
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent={!isMobile ? 'space-between' : 'flex-end'}
                width="100%"
                gridGap={{ _: 'tiny', md: '0' }}
                pr={{ _: '4xsmall', md: 'medium' }}
                pl={{ _: '0', md: 'medium' }}
              >
                {!isMobile && (
                  <Box>
                    <Typography size="heading-18" color="grey-900">
                      {businessName}
                    </Typography>
                    <Typography size="caption-12" color="grey-500">
                      {registrationNumber}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={{ _: '4xsmall', md: 'xsmall' }}>
                  {showPlans && (
                    <Button
                      onClick={handleComparePlansClick}
                      size="large"
                      variant="outlined"
                      baseColor="green"
                      data-testid="upgrade-pro-header"
                    >
                      Compare plans
                    </Button>
                  )}
                  <CloudConnection />
                  <UserMenu
                    onDemasquerade={isMasquerade && !isClientView ? demasquerade : undefined}
                    logoutCallback={logout}
                    name={userName}
                    email={userEmail}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </GridItem>
      </Grid>
    </FlexGridedHeader>
  );
};

Header.propTypes = {
  menuClickCallback: PropTypes.func,
  isOpen: PropTypes.bool,
  didScroll: PropTypes.bool,
  injectOrigin: PropTypes.bool,
  contentRef: PropTypes.any,
};

export default Header;
